import { Controller } from "stimulus"
import { prepareSession, pageSessionHeaders, authenticationFailure } from "../session_recovery"

export default class extends Controller {
  connect() {
    this.dragging = null
    this.pendingOrders = []
    this.observer = new MutationObserver(() => {
      this.prepareItems()
      this.updatePositions()
    })
    this.observer.observe(this.element, { childList: true })
    this.form = this.element.closest("form")
    if (this.form) this.form.addEventListener("submit", this.updatePositions)
    this.updatePositions()
    this.prepareItems()

    this.element.addEventListener("dragover", this.over)
    this.element.addEventListener("drop", this.drop)
  }

  prepareItems() {
    this.items.forEach((item) => {
      item.draggable = true
      item.addEventListener("dragstart", this.start)
      item.addEventListener("dragend", this.end)
    })
  }

  disconnect() {
    this.observer.disconnect()
    if (this.saveNotice) this.saveNotice.remove()
    if (this.form) this.form.removeEventListener("submit", this.updatePositions)
    this.items.forEach((item) => {
      item.draggable = false
      item.removeEventListener("dragstart", this.start)
      item.removeEventListener("dragend", this.end)
    })

    this.element.removeEventListener("dragover", this.over)
    this.element.removeEventListener("drop", this.drop)
  }

  start = (event) => {
    this.dragging = event.currentTarget
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", this.dragging.id || this.dragging.dataset.itemId)
    this.dragging.classList.add("active-item-shadow")
  }

  over = (event) => {
    event.preventDefault()

    const afterElement = this.itemAfter(event.clientY)
    if (!this.dragging) return

    if (afterElement == null) {
      this.element.appendChild(this.dragging)
    } else {
      this.element.insertBefore(this.dragging, afterElement)
    }
  }

  drop = (event) => {
    event.preventDefault()
    this.persist()
  }

  end = () => {
    if (this.dragging) {
      this.dragging.classList.remove("active-item-shadow")
    }
    this.dragging = null
  }

  persist() {
    if (this.mode === "activity") {
      this.persistActivity()
    } else {
      this.persistOrder()
    }
  }

  moveUp(event) {
    this.move(event, -1)
  }

  moveDown(event) {
    this.move(event, 1)
  }

  move(event, direction) {
    event.preventDefault()
    const item = event.currentTarget.closest(this.itemSelector)
    const visibleItems = this.items.filter((element) => element.style.display !== "none")
    const index = visibleItems.indexOf(item)
    const neighbor = visibleItems[index + direction]
    if (!item || index < 0 || !neighbor) return

    if (direction < 0) {
      this.element.insertBefore(item, neighbor)
    } else {
      this.element.insertBefore(neighbor, item)
    }
    this.persistOrder()
    event.currentTarget.focus()
  }

  updatePositions = () => {
    this.items.forEach((item, index) => {
      const input = item.querySelector("[data-sortable-position]")
      if (input) input.value = index
    })
  }

  persistOrder() {
    this.updatePositions()
    const ids = this.items.map((item) => item.dataset.itemId).filter(Boolean)

    this.saveOrder(JSON.stringify({ ids }), "application/json")
  }

  persistActivity() {
    if (!this.dragging) return
    const body = new URLSearchParams()
    body.append("activity_params[activity_id]", this.dragging.dataset.itemId)
    body.append("activity_params[activity_position_order]", this.items.indexOf(this.dragging))
    this.saveOrder(body)
  }

  async saveOrder(body, contentType) {
    // Activity requests describe individual moves: retain each until acknowledged.
    // Full-order requests use the same queue to keep writes in user-action order.
    if (body !== undefined) this.pendingOrders.push({ body, contentType })
    if (this.savingOrder) return
    this.savingOrder = true
    this.orderNotice("Saglabā secību...")
    try {
      await prepareSession()
      while (this.pendingOrders.length) {
        const pending = this.pendingOrders[0]
        const response = await fetch(this.url, {
          method: "POST", credentials: "same-origin",
          headers: {
            Accept: "application/json", "X-CSRF-Token": this.csrfToken(),
            ...pageSessionHeaders(), ...(pending.contentType ? { "Content-Type": pending.contentType } : {})
          },
          body: pending.body
        })
        const data = response.ok ? null : await response.json().catch(() => null)
        if (authenticationFailure(response, data) || !response.ok) throw new Error("order_not_saved")
        this.pendingOrders.shift()
      }
      this.orderNotice("Secība saglabāta.")
    } catch (_error) {
      this.orderNotice("Secība nav saglabāta. Pārbaudiet savienojumu un sesiju, tad mēģiniet vēlreiz.", true)
    } finally {
      this.savingOrder = false
    }
  }

  orderNotice(message, retry = false) {
    if (!this.saveNotice) {
      this.saveNotice = document.createElement("div")
      this.saveNotice.setAttribute("role", "status")
      const anchor = this.element.closest("table") || this.element
      anchor.after(this.saveNotice)
    }
    this.saveNotice.textContent = message
    if (retry) {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "btn btn-outline-secondary"
      button.textContent = "Atkārtot secības saglabāšanu"
      button.addEventListener("click", () => {
        if (this.pendingOrders.length) this.saveOrder()
      })
      this.saveNotice.appendChild(button)
    }
  }

  itemAfter(y) {
    return this.items
      .filter((item) => item !== this.dragging)
      .reduce((closest, item) => {
        const box = item.getBoundingClientRect()
        const offset = y - box.top - box.height / 2

        if (offset < 0 && offset > closest.offset) {
          return { offset, element: item }
        }

        return closest
      }, { offset: Number.NEGATIVE_INFINITY }).element
  }

  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta && meta.content
  }

  get items() {
    const items = this.itemSelector
      ? Array.from(this.element.querySelectorAll(this.itemSelector))
      : Array.from(this.element.children)

    return items
      .filter((item) => item.parentElement === this.element)
  }

  get itemSelector() {
    return this.data.get("itemSelector")
  }

  get mode() {
    return this.data.get("mode") || "order"
  }

  get url() {
    return this.data.get("url")
  }
}
